import type {
  CoachingPlan,
  CoachingSession,
  CoachingSessionType,
} from '@efdigital/ll-addon-data';
import { AdminSessionsRequestError } from '@efdigital/ll-admin-v2/data/builders/coaching/admin-sessions';
import eq from '@efdigital/ll-admin-v2/helpers/eq.ts';
import type AdminSessionsService from '@efdigital/ll-admin-v2/services/admin-sessions';
import type { AdminPostQuotaGrantRequestBody } from '@efdigital/ll-cdk-coaching-sessions-api-types';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import BsModal from 'ember-bootstrap/components/bs-modal';

interface SessionQuotaGrantModalSignature {
  Args: {
    coachingPlan: CoachingPlan;
    coachingSession: CoachingSession;
    onSuccess: () => void | Promise<void>;
  };
  Blocks: {
    grant: [showModal: () => void];
  };
}

const SESSION_TYPE_LABELS: Record<CoachingSessionType, string> = {
  chemistry: 'Chemistry',
  coaching: 'Coaching',
};

export class SessionQuotaGrantModal extends Component<SessionQuotaGrantModalSignature> {
  @service declare adminSessions: AdminSessionsService;

  @tracked amount = 1;
  @tracked isSubmitting = false;
  @tracked sessionType: CoachingSessionType = 'coaching';

  idempotencyKey = '';

  @tracked visible = false;

  openModal = () => {
    this.idempotencyKey = crypto.randomUUID();
    this.sessionType = this.args.coachingSession.sessionType ?? 'coaching';
    this.amount = this.sessionType === 'coaching' ? 60 : 1;
    this.visible = true;
  };

  closeModal = () => {
    this.visible = false;
  };

  updateAmount = (event: Event) => {
    const value = Number.parseInt((event.target as HTMLInputElement).value, 10);
    this.amount = Number.isFinite(value) ? value : 1;
  };

  selectSessionType = (event: Event) => {
    const value = (event.target as HTMLInputElement).value;
    if (value === 'chemistry' || value === 'coaching') {
      this.sessionType = value;
      this.amount = value === 'coaching' ? 60 : 1;
    }
  };

  get amountLabel(): string {
    return this.sessionType === 'coaching' ? 'Minutes' : 'Calls';
  }

  get amountStep(): number {
    return this.sessionType === 'coaching' ? 15 : 1;
  }

  submit = async (event: SubmitEvent) => {
    event.preventDefault();

    if (
      this.isSubmitting ||
      !this.args.coachingPlan.id ||
      !this.args.coachingSession.id
    ) {
      return;
    }

    this.isSubmitting = true;

    try {
      const body: AdminPostQuotaGrantRequestBody = {
        amount: this.amount,
        sessionType: this.sessionType,
        coachingSessionId: this.args.coachingSession.id,
        reason: 'admin_grant',
      };

      await this.adminSessions.grantQuota(
        this.args.coachingPlan.id,
        this.idempotencyKey,
        body
      );

      await this.args.onSuccess();
      this.closeModal();
    } catch (error) {
      if (error instanceof AdminSessionsRequestError && error.status === 409) {
        // This grant was already recorded with the same idempotency key.
      }
    } finally {
      this.isSubmitting = false;
    }
  };

  <template>
    {{yield this.openModal to="grant"}}
    <BsModal
      @open={{this.visible}}
      @onHidden={{this.closeModal}}
      @size="lg"
      @renderInPlace={{true}}
      @backdropClose={{false}}
      as |modal|
    >
      <modal.header>
        <h2 class="modal-title h5">Grant quota</h2>
      </modal.header>
      <modal.body>
        <form data-test-session-quota-grant-form {{on "submit" this.submit}}>
          <p class="text-body-secondary mb-3">
            Credit additional quota to this coaching plan. The grant will be
            linked to this session in the quota ledger.
          </p>

          <fieldset class="mb-3">
            <div class="row">
              <div class="col">
                <legend class="gud-eyebrow">Quota pool</legend>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="radio"
                    name="quota-grant-session-type"
                    id="quota-grant-session-type-coaching"
                    value="coaching"
                    checked={{if (eq this.sessionType "coaching") true false}}
                    disabled={{this.isSubmitting}}
                    data-test-quota-grant-session-type="coaching"
                    {{on "change" this.selectSessionType}}
                  />
                  <label
                    class="form-check-label"
                    for="quota-grant-session-type-coaching"
                  >{{SESSION_TYPE_LABELS.coaching}}</label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="radio"
                    name="quota-grant-session-type"
                    id="quota-grant-session-type-chemistry"
                    value="chemistry"
                    checked={{if (eq this.sessionType "chemistry") true false}}
                    disabled={{this.isSubmitting}}
                    data-test-quota-grant-session-type="chemistry"
                    {{on "change" this.selectSessionType}}
                  />
                  <label
                    class="form-check-label"
                    for="quota-grant-session-type-chemistry"
                  >{{SESSION_TYPE_LABELS.chemistry}}</label>
                </div>
              </div>
              <div class="col">

                <div class="mb-3">
                  <label
                    class="form-label"
                    for="quota-grant-amount"
                  >{{this.amountLabel}}</label>
                  <input
                    class="form-control"
                    id="quota-grant-amount"
                    type="number"
                    min="1"
                    step={{this.amountStep}}
                    value={{this.amount}}
                    disabled={{this.isSubmitting}}
                    data-test-quota-grant-amount
                    {{on "input" this.updateAmount}}
                  />
                </div>
              </div>
            </div>
          </fieldset>

          <button
            type="submit"
            class="btn btn-primary"
            disabled={{this.isSubmitting}}
            data-test-quota-grant-submit
          >
            {{if this.isSubmitting "Granting…" "Grant quota"}}
          </button>
        </form>
      </modal.body>
    </BsModal>
  </template>
}
